import { useFormikContext } from "formik";
import { keepPreviousData } from "@tanstack/react-query";
import { z } from "zod";
import { toast } from "sonner";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { SelectInput } from "@/lib/Components/Form/Inputs/SelectInput";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { DateTimeInput } from "@/lib/Components/Form/Inputs/DateTimeInput";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import {
  createBookingChangeRequestMutation,
  sendBookingChangeRequestPaymentRequestMutation,
} from "@/app/Bookings/GraphQL/bookingChangeRequestMutations";
import { bookingChangeRequestQuoteQuery } from "@/app/Bookings/GraphQL/bookingChangeRequestQuoteQuery";
import { BookingChangeRequestType } from "@/gql/graphql";
import { cn } from "@/lib/utils";
import { BookingRecord } from "@/app/Bookings/GraphQL/bookingRecordQuery";

type FormValues = {
  type: BookingChangeRequestType;
  depart_at?: string | null;
  deliver_at?: string | null;
  name?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
  number_of_travellers?: number | null;
  request?: string | null;
};

const validationSchema = z
  .object({
    type: z.enum(BookingChangeRequestType),
    depart_at: z.string().nullish(),
    deliver_at: z.string().nullish(),
    name: z.string().nullish(),
    phone: z.string().nullish(),
    date_of_birth: z.string().nullish(),
    number_of_travellers: z.number().nullish(),
    request: z.string().nullish(),
  })
  .superRefine((values, ctx) => {
    if (
      values.type === BookingChangeRequestType.Other &&
      !values.request?.trim()
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["request"],
        message: "Describe the requested change",
      });
    }
  });

function buildInput(values: FormValues) {
  switch (values.type) {
    case BookingChangeRequestType.DateChange:
      return {
        type: values.type,
        depart_at: values.depart_at,
        deliver_at: values.deliver_at,
      };
    case BookingChangeRequestType.DriverDetails:
      return {
        type: values.type,
        name: values.name,
        phone: values.phone,
        date_of_birth: values.date_of_birth,
        number_of_travellers: values.number_of_travellers,
      };
    default:
      return {
        type: values.type,
        request: values.request,
      };
  }
}

export function BookingChangeRequestCreateDialog({
  booking,
}: {
  booking: BookingRecord;
}) {
  const { mutateAsync: create } = useGqlMutation(
    createBookingChangeRequestMutation,
  );
  const { mutateAsync: sendPaymentRequest } = useGqlMutation(
    sendBookingChangeRequestPaymentRequestMutation,
  );
  const { close } = useDialogState();

  return (
    <BaseDialog
      title="Create change request"
      subtitle="Creates a change request and emails the customer a link to pay. Once paid, you can review and apply it."
    >
      <GenericForm<FormValues>
        validationSchema={toFormikValidationSchema(validationSchema)}
        initialValues={{
          type: BookingChangeRequestType.DateChange,
          depart_at: booking.depart_at,
          deliver_at: booking.deliver_at,
          name: booking.name,
          phone: booking.phone,
          date_of_birth: null,
          number_of_travellers: null,
          request: "",
        }}
        onSubmit={async (values) => {
          const { createBookingChangeRequest } = await create({
            bookingUuid: booking.uuid,
            input: buildInput(values),
          });

          await sendPaymentRequest({ id: createBookingChangeRequest.id });

          toast("Change request sent", {
            description: `Payment link for ${formatCurrency(
              createBookingChangeRequest.amount,
              createBookingChangeRequest.currency,
            )} emailed to ${booking.email}`,
          });

          return close();
        }}
      >
        <SelectInput
          name="type"
          label="Change type"
          className="col-span-full"
          options={[
            {
              label: "Change type",
              options: [
                {
                  value: BookingChangeRequestType.DateChange,
                  label: "Date change",
                },
                {
                  value: BookingChangeRequestType.DriverDetails,
                  label: "Driver details",
                },
                { value: BookingChangeRequestType.Other, label: "Other" },
              ],
            },
          ]}
        />

        <ChangeRequestFields bookingUuid={booking.uuid} />

        <DialogSubmitButton text="Create & email customer" />
      </GenericForm>
    </BaseDialog>
  );
}

function ChangeRequestFields({ bookingUuid }: { bookingUuid: string }) {
  const { values } = useFormikContext<FormValues>();

  switch (values.type) {
    case BookingChangeRequestType.DateChange:
      return (
        <>
          <DateTimeInput
            name="depart_at"
            label="New departure"
            className="col-span-full lg:col-span-6"
          />
          <DateTimeInput
            name="deliver_at"
            label="New delivery"
            className="col-span-full lg:col-span-6"
          />
          <DateChangeQuote bookingUuid={bookingUuid} />
        </>
      );
    case BookingChangeRequestType.DriverDetails:
      return (
        <>
          <TextInput
            name="name"
            label="Driver name"
            className="col-span-full lg:col-span-6"
          />
          <TextInput
            name="phone"
            label="Phone"
            className="col-span-full lg:col-span-6"
          />
          <DateInput
            name="date_of_birth"
            label="Date of birth"
            className="col-span-full lg:col-span-6"
          />
          <NumberInput
            name="number_of_travellers"
            label="Number of travellers"
            className="col-span-full lg:col-span-6"
          />
        </>
      );
    default:
      return (
        <TextAreaInput
          name="request"
          label="Request"
          className="col-span-full"
          description="Describe the change the customer is requesting"
        />
      );
  }
}

function DateChangeQuote({ bookingUuid }: { bookingUuid: string }) {
  const { values } = useFormikContext<FormValues>();
  const enabled = !!values.depart_at && !!values.deliver_at;

  const { data, isFetching } = useGqlQuery(
    bookingChangeRequestQuoteQuery,
    {
      bookingUuid,
      input: {
        type: BookingChangeRequestType.DateChange,
        depart_at: values.depart_at,
        deliver_at: values.deliver_at,
      },
    },
    { enabled, placeholderData: keepPreviousData },
  );

  if (!enabled) {
    return null;
  }

  const quote = data?.bookingChangeRequestQuote;

  return (
    <div className="col-span-full">
      <div
        className={cn(
          "rounded-lg bg-gray-50 p-4 ring-1 ring-gray-900/5 transition-opacity",
          isFetching ? "opacity-60" : "opacity-100",
        )}
      >
        {quote ? (
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Cost difference</dt>
              <dd className="font-medium text-gray-900">
                {formatCurrency(quote.price_delta_amount, quote.currency)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Change fee</dt>
              <dd className="font-medium text-gray-900">
                {formatCurrency(quote.admin_fee_amount, quote.currency)}
              </dd>
            </div>
            <div className="flex justify-between border-t border-gray-900/10 pt-2">
              <dt className="font-semibold text-gray-900">
                Total to request from customer
              </dt>
              <dd className="text-base font-semibold text-indigo-600">
                {formatCurrency(quote.amount, quote.currency)}
              </dd>
            </div>
          </dl>
        ) : (
          <div className="text-sm text-gray-500">Calculating cost…</div>
        )}
      </div>
    </div>
  );
}
